import { h, Host } from "@stencil/core";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
import { tokenMap } from "../../common/utils";
/**
 * @status experimental
 */
export class ChRating {
    constructor() {
        this.accessibleName = undefined;
        this.disabled = false;
        this.name = undefined;
        this.stars = 5;
        this.value = 0;
    }
    #accessibleNameFromExternalLabel;
    #starsArray = [];
    internals;
    el;
    startsChanged() {
        this.#computeArray();
        this.#updateFormValue();
    }
    valueChanged() {
        this.#updateFormValue();
    }
    /**
     * The `input` event is emitted when a change to the element's value is
     * committed by the user.
     *
     * It contains the new value of the control.
     */
    input;
    #computeArray = () => {
        this.#starsArray = [...Array(Math.max(0, this.stars)).keys()];
    };
    #syncValue = (event) => {
        event.stopPropagation();
        this.value = Number(event.target.value);
        this.#updateFormValue();
        this.input.emit(this.value);
    };
    #calculateValue = (calculatedMaxValue) => Math.min(Math.max(this.value, 0), // At least 0
    calculatedMaxValue // At most this.maxValue
    );
    // Max value should not be negative
    #calculateMaxValue = () => Math.max(this.stars, 0);
    #updateFormValue = () => this.internals.setFormValue(`${this.#calculateValue(this.#calculateMaxValue())}`);
    #renderStar = (calculatedValue, index) => {
        const starValue = Math.min(Math.max(0, calculatedValue - index), // At least 0
        1 // At most 1
        );
        const selected = starValue === 1;
        const startIndex = index + 1;
        return (h("div", { key: index, class: "star-container", part: tokenMap({
                "star-container": true,
                selected: selected,
                unselected: starValue === 0,
                "partial-selected": starValue !== 0 && starValue !== 1
            }), style: {
                "--star-selected-value": `${starValue}`
            } }, h("div", { class: "star", part: tokenMap({
                star: true,
                selected: selected,
                unselected: starValue === 0,
                "partial-selected": starValue !== 0 && starValue !== 1
            }) }), h("input", { id: `${startIndex}`, "aria-label": `${startIndex} stars`, name: "star", checked: selected, disabled: this.disabled, type: "radio", value: startIndex })));
    };
    connectedCallback() {
        // Accessibility
        this.#updateFormValue();
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        this.#computeArray();
        // Report any accessibility issue. // TODO: It should take into account the "caption" properties
        analyzeLabelExistence(this.el, "ch-rating", labels, this.#accessibleNameFromExternalLabel, this.accessibleName);
        this.el.setAttribute("role", "radiogroup");
    }
    render() {
        const calculatedMaxValue = this.#calculateMaxValue();
        const calculatedValue = this.#calculateValue(calculatedMaxValue);
        return (h(Host, { key: 'b78f4da26e643232b383f9c138481850d4c4b7a8', "aria-label": !this.#accessibleNameFromExternalLabel ||
                this.#accessibleNameFromExternalLabel !== this.accessibleName
                ? this.accessibleName
                : null }, h("div", { key: '39d8f50726c713dd3fd3802f669b56a7148ea815', class: "container", part: "stars-container", onInput: this.disabled ? undefined : this.#syncValue }, this.#starsArray.map(index => this.#renderStar(calculatedValue, index)))));
    }
    static get is() { return "ch-rating"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["rating.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["rating.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute allows you specify if the element is disabled.\nIf disabled, it will not trigger any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the `name` of the control when used in a form."
                },
                "attribute": "name",
                "reflect": true
            },
            "stars": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property determine the number of stars displayed."
                },
                "attribute": "stars",
                "reflect": false,
                "defaultValue": "5"
            },
            "value": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current value displayed by the component."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get events() {
        return [{
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is emitted when a change to the element's value is\ncommitted by the user.\n\nIt contains the new value of the control."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "stars",
                "methodName": "startsChanged"
            }, {
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
